import { Skeleton } from '@chakra-ui/react';
import React from 'react';
import { Navigate, Outlet, RouteObject } from 'react-router-dom';

import { AppHeader, LayoutWithSidebar } from 'packages/common-frontend/layout';
import { AnalyticsUserRouter } from '~/analytics/routes/Router';
import { BackupLenderRouter } from '~/backup/lender/routes/Router';
import { BackupOriginatorRouter } from '~/backup/originator/routes/Router';
import { BorrowerRouter } from '~/borrower/routes/Router';
import { CapitalProviderRouter } from '~/capitalProvider/routes/Router';
import { CreditFacilitiesProvider } from '~/contexts/CreditFacilitiesProvider/CreditFacilitiesProvider';
import {
  Logout,
  useUserAuthUnsafe,
  useUserAuthOrThrow,
  useRedirectToLogin,
} from '~/contexts/UserAuthProvider';
import { DefaultErrorRoute } from '~/core/DefaultErrorRoute';
import { PermissionGuard } from '~/core/PermissionGuard';
import { Providers } from '~/core/Providers';
import { Router as AnalyticsRouter } from '~/core/routes/analytics/Router';
import { Route as LoginRoute } from '~/core/routes/auth/login/_index';
import { Route as CalculationTerminologiesRoute } from '~/core/routes/calculation-terminologies/_index';
import { Router as CollateralDataQualityRouter } from '~/core/routes/collateral-data-quality/Router';
import { Router as DataPipelineRouter } from '~/core/routes/data-pipeline/Router';
import { Router as DeliverablesRouter } from '~/core/routes/deliverables/Router';
import { Router as DevRouter } from '~/core/routes/dev/Router';
import { Route as EventsRoute } from '~/core/routes/events/_index';
import { Router as FundingRequestsRouter } from '~/core/routes/funding-requests/Router';
import { Router as ImplementationsRouter } from '~/core/routes/implementations/Router';
import { Router as InvoicesRouter } from '~/core/routes/invoices/Router';
import { Router as LimitedPartnersRouter } from '~/core/routes/limited-partners/Router';
import { Router as LoansRouter } from '~/core/routes/loans/Router';
import { Route as OmniAnalyticsRoute } from '~/core/routes/omni-analytics/_index';
import { Route as ReportsRoute } from '~/core/routes/reports/_index';
import { Router as SettingsRouter } from '~/core/routes/settings/Router';
import { Router as TasksRouter } from '~/core/routes/tasks/Router';
import { Route as TransactionsRoute } from '~/core/routes/transactions/_index';
import { Router as ValleyBankRouter } from '~/core/routes/valley-bank/Router';
import { SettingsGlobalNav } from '~/core/SettingsGlobalNav';
import { UserAwareGlobalNav } from '~/core/UserAwareGlobalNav';

const LoggedInRouter = () => {
  const { user } = useUserAuthOrThrow();
  if (user.userType === 'BORROWER') return <BorrowerRouter />;
  if (user.userType === 'CAPITAL_PROVIDER') return <CapitalProviderRouter />;

  // Log all other users out (including SUPER_USER). Backup users are
  // hard-contained earlier in `LoginGuard` and never reach this legacy branch.
  return <Logout />;
};

const LoginGuard = ({ children }: { children: React.ReactNode }) => {
  const { status, user } = useUserAuthUnsafe();

  // Redirect logged out users to `/auth/login`
  useRedirectToLogin(status);

  if (status === 'loading') return <Skeleton />;
  if (status === 'error') return <DefaultErrorRoute />;
  if (status === 'logged-out') return <Skeleton />;

  // Super users not allowed
  if (user.userType === 'SUPER_USER') return <Logout />;

  // Analytics users only ever see the analytics-only dashboard. We short-circuit
  // here — rather than in the legacy `*` `LoggedInRouter` branch — so containment
  // applies to *all* logged-in routes, including RR7 static routes like
  // `/analytics/*` and `/settings/*`. `AnalyticsUserRouter` redirects any unknown
  // path back to its index.
  if (user.userType === 'ANALYTICS') return <AnalyticsUserRouter />;

  // Backup-servicing users are likewise contained to their own portal. We
  // short-circuit here so containment applies to *all* logged-in routes: this
  // lets the backup sub-routers own routes like `/data-pipeline` instead of the
  // shared RR7 route (which is gated behind `productsDataPipelinePermissions`
  // and would otherwise intercept the path and bounce BUS users out).
  if (user.userType === 'BACKUP_ORIGINATOR') return <BackupOriginatorRouter />;
  if (user.userType === 'BACKUP_LENDER') return <BackupLenderRouter />;

  return <>{children}</>;
};

export const routeConfig: RouteObject[] = [
  {
    element: (
      <Providers>
        <Outlet />
      </Providers>
    ),
    children: [
      {
        path: '/auth/*',
        children: [{ path: 'login', element: <LoginRoute /> }],
      },
      {
        path: '/dev/*',
        children: [DevRouter],
      },

      // Logged-in routes
      {
        element: (
          <LoginGuard>
            <Outlet />
          </LoginGuard>
        ),
        children: [
          // Routes that have been fully migrated to RR7 static routing
          {
            element: (
              <LayoutWithSidebar
                header={
                  <AppHeader
                    HeaderRowProps={{ FlexProps: { position: 'relative' } }}
                  />
                }
                globalNav={<UserAwareGlobalNav isFixed={false} />}
              >
                <CreditFacilitiesProvider>
                  <Outlet />
                </CreditFacilitiesProvider>
              </LayoutWithSidebar>
            ),
            children: [
              {
                path: '/analytics/*',
                children: [AnalyticsRouter],
              },
              {
                path: '/omni-analytics',
                element: <OmniAnalyticsRoute />,
              },
              {
                path: '/calculation-terminologies',
                element: <CalculationTerminologiesRoute />,
              },
              {
                path: '/collateral-data-quality',
                children: [CollateralDataQualityRouter],
              },
              {
                path: '/data-pipeline/*',
                children: [DataPipelineRouter],
              },
              {
                path: '/deliverables/*',
                children: [DeliverablesRouter],
              },
              {
                path: '/events',
                element: <EventsRoute />,
              },
              {
                path: '/funding-requests/*',
                children: [FundingRequestsRouter],
              },
              {
                path: '/implementations/*',
                children: [ImplementationsRouter],
              },
              {
                path: '/invoices/*',
                children: [InvoicesRouter],
              },
              {
                path: '/limited-partners/*',
                children: [LimitedPartnersRouter],
              },
              {
                path: '/loans/*',
                children: [LoansRouter],
              },
              {
                path: '/reports',
                element: <ReportsRoute />,
              },
              {
                path: '/tasks/*',
                children: [TasksRouter],
              },
              {
                path: '/transactions/*',
                element: <TransactionsRoute />,
              },
              // Valley Bank routes
              {
                path: '/valley-bank/*',
                children: [ValleyBankRouter],
              },
            ],
          },

          // Settings routes, migrated to RR7 static routing, but different global nav options
          {
            element: (
              <LayoutWithSidebar
                header={
                  <AppHeader
                    HeaderRowProps={{ FlexProps: { position: 'relative' } }}
                  />
                }
                globalNav={<SettingsGlobalNav isFixed={false} />}
              >
                <CreditFacilitiesProvider>
                  <Outlet />
                </CreditFacilitiesProvider>
              </LayoutWithSidebar>
            ),
            children: [
              {
                path: '/settings/*',
                children: [SettingsRouter],
              },
            ],
          },
          {
            element: (
              // Valley Bank routes have a different home route. If we don't handle it manually,
              // users will hit the `*` capital provider route, and we definitely don't want that.
              // Once all routes are migrated to static routing, the need for this will go away.
              <PermissionGuard
                permissions={(context) =>
                  context.user.organization.slug === 'valleyBank'
                    ? 'deny'
                    : 'allow'
                }
                fallback={<Navigate to="/valley-bank" replace />}
              >
                <Outlet />
              </PermissionGuard>
            ),

            // Legacy routes that have not yet been migrated to RR7 static routing
            children: [
              {
                path: '*',
                element: <LoggedInRouter />,
              },
            ],
          },
        ],
      },
      {
        path: '*',
        element: <Navigate to="/" replace />,
      },
    ],
  },
];
